import {
  Component,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { AuthLayoutComponent } from '../layout/auth-layout.component';
import { LoggerService, ValidationUtils } from '@victory-lib-ui/shared/utils';
import { ToastService } from '@victory-lib-ui/shared/utils';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  extractValidationErrors,
  flattenValidationErrors,
  resolveAuthFailureMessageKey,
  resolvePasswordRequirementMessageKey,
} from '../../utils/auth-feedback.utils';

@Component({
  selector: 'lib-register',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterLink,
    AuthLayoutComponent,
    TranslocoModule,
  ],
  templateUrl: './register.component.html',
  styleUrls: ['../../styles/auth-card.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegisterComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly logger = inject(LoggerService);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(TranslocoService);

  showPassword = signal(false);
  showConfirmPassword = signal(false);
  isLoading = signal(false);
  errorMessage = signal<string | null>(null);
  fieldErrors = signal<Record<string, string>>({});
  passwordFieldActive = signal(false);

  private passwordValue = signal('');

  registerForm: FormGroup;

  passwordRequirements = computed(() => {
    const password = this.passwordValue();
    return ValidationUtils.getPasswordRequirements(password);
  });

  passwordIsStrong = computed(() => {
    const reqs = this.passwordRequirements();
    return (
      reqs.hasMinLength &&
      reqs.hasUppercase &&
      reqs.hasLowercase &&
      reqs.hasNumber &&
      reqs.hasSpecialChar
    );
  });

  constructor() {
    this.registerForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required]],
      confirmPassword: ['', [Validators.required]],
      agreed_to_terms: [false, [Validators.requiredTrue]],
    });

    this.registerForm.get('password')?.valueChanges.subscribe((value) => {
      this.passwordValue.set(value ?? '');
    });
  }

  togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
    if (field === 'password') {
      this.showPassword.update((show) => !show);
    } else {
      this.showConfirmPassword.update((show) => !show);
    }
  }

  get passwordsMatch(): boolean {
    const password = this.registerForm.get('password')?.value ?? '';
    const confirmPassword = this.registerForm.get('confirmPassword')?.value ?? '';
    return password === confirmPassword && password.length > 0;
  }

  onSubmit(): void {
    if (this.registerForm.invalid) {
      this.markFormGroupTouched(this.registerForm);
      const key = 'register.error_validation';
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    if (!this.passwordIsStrong()) {
      const key = resolvePasswordRequirementMessageKey(
        'register',
        this.password?.value ?? ''
      );
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    if (!this.passwordsMatch) {
      const key = 'register.passwords_mismatch';
      this.errorMessage.set(this.i18n.translate(key));
      this.toast.show(key, 'error');
      return;
    }

    this.isLoading.set(true);
    this.errorMessage.set(null);
    this.fieldErrors.set({});

    const formValue = this.registerForm.value;

    this.authService.register(formValue).subscribe({
      next: (response) => {
        if (response.success) {
          this.logger.info(
            'Registration successful - redirecting to dashboard'
          );
          this.router.navigate(['/dashboard']);
        } else {
          if (response.errors) {
            this.fieldErrors.set(flattenValidationErrors(response.errors));
          }
          const key = response.errors
            ? 'register.error_validation'
            : 'register.error_unexpected';
          this.errorMessage.set(this.i18n.translate(key));
          this.toast.show(key, 'error');
          this.isLoading.set(false);
        }
      },
      error: (error) => {
        this.logger.error('Registration error', error);
        this.fieldErrors.set(extractValidationErrors(error));
        const key = resolveAuthFailureMessageKey('register', error);
        this.errorMessage.set(this.i18n.translate(key));
        this.toast.show(key, 'error');
        this.isLoading.set(false);
      },
    });
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  get name() {
    return this.registerForm.get('name');
  }

  get email() {
    return this.registerForm.get('email');
  }

  get password() {
    return this.registerForm.get('password');
  }

  get confirmPassword() {
    return this.registerForm.get('confirmPassword');
  }

  get termsAgreed() {
    return this.registerForm.get('agreed_to_terms');
  }

  onSocialRegister(provider: string): void {
    this.toast.show(
      this.i18n.translate('register.social_coming_soon', { provider }),
      'info'
    );
  }
}
